import { useEffect, useState } from 'react'
import type { MaterialDto, PastaMaterialDto, UsuarioSessao } from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEnviarMaterial, useMateriais } from '../../api/hooks-materiais'
import { pode } from '../../permissao'
import { BotaoIcone } from '../../componentes/BotaoIcone'
import {
  IconeArquivo,
  IconeBaixar,
  IconeEnviar,
  IconeImagem,
  IconePasta,
  IconeVideo,
} from '../../componentes/icones'
import { Aviso, Carregando, Modal, juntar } from '../../componentes/ui'

/**
 * Biblioteca de materiais, aberta de dentro da conversa.
 *
 * A pergunta que ela responde é "o cliente pediu a foto da escola, cadê?".
 * Por isso não virou página: sair da conversa para procurar o arquivo, achar,
 * baixar no computador e anexar de volta são quatro passos para mandar uma
 * foto que a empresa já tem pronta.
 *
 * Nada sai no clique. O clique abre a prévia ampliada, e o envio é um segundo
 * botão dentro dela — mandar o arquivo errado para o cliente é do tipo de
 * engano que não tem desfazer.
 *
 * Aqui só se procura, confere, baixa e envia. Criar pasta, subir, renomear e
 * apagar moram na Configuração, aba Materiais: no meio do atendimento, um
 * lápis e uma lixeira ao lado de cada arquivo eram só chance de apagar o que
 * a equipe inteira usa.
 */

export function IconeDoTipo({
  tipo,
  className,
}: {
  tipo: MaterialDto['tipo']
  className?: string
}) {
  if (tipo === 'imagem') return <IconeImagem className={className} />
  if (tipo === 'video') return <IconeVideo className={className} />
  return <IconeArquivo className={className} />
}

export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

/** Botão do compositor + o painel. Fica ao lado do clipe de anexo. */
export function BotaoMateriais({
  chamadoId,
  usuario,
  desabilitado,
}: {
  chamadoId: string
  usuario: UsuarioSessao
  desabilitado?: boolean
}) {
  const [aberto, setAberto] = useState(false)

  if (!pode(usuario, 'materiais.ver')) return null

  return (
    <>
      <BotaoIcone
        rotulo="Materiais"
        onClick={() => setAberto(true)}
        disabled={desabilitado}
        className={aberto ? 'bg-tinta-100 text-tinta-900' : undefined}
      >
        <IconePasta className="h-5 w-5" />
      </BotaoIcone>

      <PainelDeMateriais
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        chamadoId={chamadoId}
        usuario={usuario}
      />
    </>
  )
}

export function PainelDeMateriais({
  aberto,
  aoFechar,
  chamadoId,
  usuario,
}: {
  aberto: boolean
  aoFechar: () => void
  /** Sem conversa aberta, o painel só baixa: não há para quem enviar. */
  chamadoId: string | null
  usuario: UsuarioSessao
}) {
  const { data: pastas, isLoading } = useMateriais(aberto)
  // Quem organiza a biblioteca vê, na biblioteca vazia, onde subir os arquivos.
  const podeOrganizar = pode(usuario, 'materiais.editar')

  const [pastaAberta, setPastaAberta] = useState<string | null>(null)
  const [ampliado, setAmpliado] = useState<MaterialDto | null>(null)
  const [busca, setBusca] = useState('')

  // Uma pasta só: abrir já dentro dela poupa um clique que não decide nada.
  useEffect(() => {
    if (!aberto) return
    if (pastas && pastas.length === 1) setPastaAberta(pastas[0]?.id ?? null)
  }, [aberto, pastas])

  useEffect(() => {
    if (!aberto) {
      setPastaAberta(null)
      setBusca('')
    }
  }, [aberto])

  const pasta = pastas?.find((p) => p.id === pastaAberta) ?? null

  return (
    <>
      <Modal aberto={aberto && !ampliado} titulo="Materiais" aoFechar={aoFechar}>
        {isLoading ? (
          <Carregando texto="Abrindo a biblioteca…" />
        ) : pasta ? (
          <ConteudoDaPasta
            pasta={pasta}
            aoVoltar={pastas && pastas.length > 1 ? () => setPastaAberta(null) : null}
            aoAmpliar={setAmpliado}
          />
        ) : (
          <ListaDePastas
            pastas={pastas ?? []}
            busca={busca}
            aoBuscar={setBusca}
            podeOrganizar={podeOrganizar}
            aoAbrir={setPastaAberta}
            aoAmpliar={setAmpliado}
          />
        )}
      </Modal>

      {ampliado ? (
        <MaterialAmpliado
          material={ampliado}
          chamadoId={chamadoId}
          aoFechar={() => setAmpliado(null)}
          aoEnviado={() => {
            setAmpliado(null)
            aoFechar()
          }}
        />
      ) : null}
    </>
  )
}

// ──────────────────────────── lista de pastas ────────────────────────────

function ListaDePastas({
  pastas,
  busca,
  aoBuscar,
  podeOrganizar,
  aoAbrir,
  aoAmpliar,
}: {
  pastas: PastaMaterialDto[]
  busca: string
  aoBuscar: (v: string) => void
  podeOrganizar: boolean
  aoAbrir: (id: string) => void
  aoAmpliar: (m: MaterialDto) => void
}) {
  const termo = busca.trim().toLowerCase()

  /*
    Com a busca preenchida, as pastas somem e ficam só os arquivos.

    Quem digita "gengiva" quer o arquivo, não saber em que pasta ele mora —
    obrigar a abrir a pasta certa seria devolver o problema que a busca veio
    resolver.
  */
  const encontrados = termo
    ? pastas.flatMap((p) =>
        p.materiais
          .filter(
            (m) =>
              m.nome.toLowerCase().includes(termo) ||
              (m.descricao ?? '').toLowerCase().includes(termo),
          )
          .map((m) => ({ material: m, pasta: p.nome })),
      )
    : []

  return (
    <div className="space-y-3">
      <input
        value={busca}
        onChange={(e) => aoBuscar(e.target.value)}
        placeholder="Procurar material"
        className="w-full rounded-md border border-tinta-200 px-3 py-2 text-sm outline-none focus:border-marca focus:ring-2 focus:ring-marca/20"
      />

      {termo ? (
        encontrados.length === 0 ? (
          <p className="py-8 text-center text-sm text-tinta-400">
            Nenhum material com “{busca.trim()}”.
          </p>
        ) : (
          <ul className="space-y-1">
            {encontrados.map(({ material, pasta }) => (
              <LinhaDeMaterial
                key={material.id}
                material={material}
                pasta={pasta}
                aoAmpliar={aoAmpliar}
              />
            ))}
          </ul>
        )
      ) : pastas.length === 0 ? (
        <Aviso tipo={podeOrganizar ? 'info' : 'atencao'}>
          {podeOrganizar
            ? 'A biblioteca está vazia. As pastas e os arquivos entram pela Configuração, em Materiais.'
            : 'A biblioteca ainda está vazia. Quem administra o sistema precisa subir os arquivos.'}
        </Aviso>
      ) : (
        <ul className="space-y-1">
          {pastas.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => aoAbrir(p.id)}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition hover:bg-tinta-50"
              >
                <IconePasta className="h-5 w-5 shrink-0 text-tinta-400" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-tinta-900">
                  {p.nome}
                </span>
                <span className="shrink-0 text-xs text-tinta-400">
                  {p.materiais.length === 1 ? '1 arquivo' : `${p.materiais.length} arquivos`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─────────────────────────── dentro de uma pasta ───────────────────────────

function ConteudoDaPasta({
  pasta,
  aoVoltar,
  aoAmpliar,
}: {
  pasta: PastaMaterialDto
  aoVoltar: (() => void) | null
  aoAmpliar: (m: MaterialDto) => void
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {aoVoltar ? (
          <button
            type="button"
            onClick={aoVoltar}
            className="shrink-0 rounded px-1.5 py-1 text-sm text-tinta-400 transition hover:bg-tinta-100 hover:text-tinta-900"
          >
            ‹ Pastas
          </button>
        ) : null}
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-tinta-900">
          {pasta.nome}
        </h3>
      </div>

      {pasta.materiais.length === 0 ? (
        <p className="py-8 text-center text-sm text-tinta-400">Pasta vazia.</p>
      ) : (
        <ul className="space-y-1">
          {pasta.materiais.map((m) => (
            <LinhaDeMaterial key={m.id} material={m} aoAmpliar={aoAmpliar} />
          ))}
        </ul>
      )}
    </div>
  )
}

function LinhaDeMaterial({
  material,
  pasta,
  aoAmpliar,
}: {
  material: MaterialDto
  /** Só na busca, onde a pasta não está na tela. */
  pasta?: string
  aoAmpliar: (m: MaterialDto) => void
}) {
  return (
    <li className="group flex items-center gap-2 rounded-md hover:bg-tinta-50">
      <button
        type="button"
        onClick={() => aoAmpliar(material)}
        className="flex min-w-0 flex-1 items-center gap-3 px-2 py-2 text-left"
      >
        <MiniaturaDoMaterial material={material} />

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-tinta-900">{material.nome}</span>
          <span className="block truncate text-xs text-tinta-400">
            {pasta ? `${pasta} · ` : ''}
            {tamanhoLegivel(material.tamanho)}
            {material.descricao ? ` · ${material.descricao}` : ''}
          </span>
        </span>
      </button>

      <a
        href={`${material.arquivoUrl}?baixar=1`}
        download={material.nomeArquivo}
        className="mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-tinta-400 transition hover:bg-tinta-100 hover:text-tinta-900"
        aria-label={`Baixar ${material.nome}`}
        title="Baixar no computador"
      >
        <IconeBaixar className="h-4 w-4" />
      </a>
    </li>
  )
}

/**
 * A miniatura é a própria imagem, e não um ícone genérico.
 *
 * Em pasta de "fotos da escola" os nomes se parecem todos, e quem procura
 * reconhece a peça de olho. O ícone obrigaria a abrir a prévia de cada uma
 * para descobrir qual é qual.
 */
export function MiniaturaDoMaterial({ material }: { material: MaterialDto }) {
  if (material.tipo === 'imagem') {
    return (
      <img
        src={material.arquivoUrl}
        alt=""
        loading="lazy"
        className="h-10 w-10 shrink-0 rounded object-cover ring-1 ring-tinta-200"
      />
    )
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-tinta-100 text-tinta-400">
      <IconeDoTipo tipo={material.tipo} className="h-5 w-5" />
    </span>
  )
}

// ───────────────────────────── prévia ampliada ─────────────────────────────

/**
 * A prévia é onde o envio acontece, e o botão fica longe do clique que abriu.
 *
 * O material foi aberto para ser conferido: é aqui que se vê que a foto é a
 * certa. Mandar direto da lista transformaria um clique de
 * curiosidade em mensagem no WhatsApp do cliente.
 */
export function MaterialAmpliado({
  material,
  chamadoId,
  aoFechar,
  aoEnviado,
}: {
  material: MaterialDto
  /** Sem conversa (na Configuração), a prévia só mostra e baixa. */
  chamadoId: string | null
  aoFechar: () => void
  aoEnviado?: () => void
}) {
  const enviar = useEnviarMaterial()
  const [confirmando, setConfirmando] = useState(false)

  /*
    Fecha com Esc, como toda janela do sistema. Só o X fechava, e quem abria
    uma foto para conferir ficava procurando a saída no canto da tela.
  */
  useEffect(() => {
    const noTeclado = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') aoFechar()
    }
    document.addEventListener('keydown', noTeclado)
    return () => document.removeEventListener('keydown', noTeclado)
  }, [aoFechar])

  const erro =
    enviar.error instanceof ErroRequisicao
      ? enviar.error.corpo.mensagem
      : enviar.isError
        ? 'Não deu para enviar agora. Tente de novo.'
        : null

  return (
    /*
      Clique no fundo escuro fecha, e clique na foto, no vídeo ou nos botões
      não: o `target` precisa ser o próprio fundo (ou a faixa em volta da
      mídia), e não algo dentro deles.
    */
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-fundo-escuro/90 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar()
      }}
    >
      <header className="flex shrink-0 items-center gap-3 px-4 py-3 text-white">
        <IconeDoTipo tipo={material.tipo} className="h-5 w-5 shrink-0 opacity-70" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{material.nome}</p>
          <p className="truncate text-xs text-white/60">
            {material.nomeArquivo} · {tamanhoLegivel(material.tamanho)}
          </p>
        </div>
        <button
          type="button"
          onClick={aoFechar}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-xl text-white/70 transition hover:bg-white/10 hover:text-white"
          aria-label="Fechar"
        >
          ×
        </button>
      </header>

      <div
        className="flex min-h-0 flex-1 items-center justify-center px-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) aoFechar()
        }}
      >
        {material.tipo === 'imagem' ? (
          <img
            src={material.arquivoUrl}
            alt={material.nome}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        ) : material.tipo === 'video' ? (
          // A prévia já ocupa a janela; o botão de tela cheia do navegador é
          // recusado nesta janela e ficaria ali parecendo quebrado.
          <video
            src={material.arquivoUrl}
            controls
            controlsList="nofullscreen"
            className="max-h-full max-w-full rounded-lg bg-black"
          />
        ) : (
          /*
            PDF abre dentro da própria janela em vez de virar link.

            Conferir antes de mandar é o ponto de toda esta tela; abrir numa
            aba nova tiraria a pessoa daqui e o botão de enviar ficaria para
            trás, no lugar de onde ela saiu.
          */
          <iframe
            src={material.arquivoUrl}
            title={material.nome}
            className="h-full w-full rounded-lg bg-superficie"
          />
        )}
      </div>

      <footer
        className="shrink-0 space-y-2 px-4 py-3"
        onClick={(e) => {
          if (e.target === e.currentTarget) aoFechar()
        }}
      >
        {material.descricao ? (
          <p className="text-center text-xs text-white/70">{material.descricao}</p>
        ) : null}

        {erro ? (
          <p className="rounded-md bg-red-500/20 px-3 py-2 text-center text-sm text-red-100">
            {erro}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-center gap-2">
          <a
            href={`${material.arquivoUrl}?baixar=1`}
            download={material.nomeArquivo}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/20"
          >
            <IconeBaixar className="h-4 w-4" />
            Baixar no computador
          </a>

          {chamadoId ? (
            confirmando ? (
              <span className="inline-flex flex-wrap items-center justify-center gap-2 rounded-md bg-white/10 px-3 py-2">
                <span className="text-sm text-white">Mandar este arquivo para o cliente?</span>
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  className="rounded-md px-2.5 py-1 text-sm text-white/70 transition hover:bg-white/10 hover:text-white"
                >
                  Não
                </button>
                <button
                  type="button"
                  disabled={enviar.isPending}
                  onClick={() =>
                    enviar.mutate(
                      { chamadoId, materialId: material.id },
                      { onSuccess: () => (aoEnviado ?? aoFechar)() },
                    )
                  }
                  className={juntar(
                    'rounded-full bg-superficie px-3 py-1 text-sm font-semibold text-tinta-900 transition hover:bg-white/90',
                    enviar.isPending && 'cursor-not-allowed opacity-60',
                  )}
                >
                  {enviar.isPending ? 'Enviando…' : 'Sim, enviar'}
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmando(true)}
                className="inline-flex items-center gap-2 rounded-full bg-marca px-4 py-2 text-sm font-semibold text-white transition hover:bg-marca-escura"
              >
                <IconeEnviar className="h-4 w-4" />
                Enviar para o cliente
              </button>
            )
          ) : null}
        </div>
      </footer>
    </div>
  )
}
